import { createApp } from 'vue'
import Chatbot from './Chatbot.vue'
import { fetchConfig } from './api'
import { readableInk, readableInkFromRgb, resolveCssColor } from './color'
import { overlayGateCss, widgetStyles } from './styles'

async function bootstrap() {
  if (typeof document === 'undefined') return

  // `document.currentScript` only works during initial script parsing. Since
  // we also defer bootstrap to DOMContentLoaded in some cases, fall back to
  // the last `script[data-product]` in the DOM.
  const current = document.currentScript as HTMLScriptElement | null
  const all = document.querySelectorAll<HTMLScriptElement>('script[data-product]')
  const self = current && current.hasAttribute('data-product') ? current : all[all.length - 1]
  if (!self) {
    // eslint-disable-next-line no-console
    console.warn('[cc-chatbot] no <script data-product="..."> found; widget not mounted')
    return
  }

  const productSlug = self.getAttribute('data-product')
  if (!productSlug) return

  // Origin derivation: prefer explicit data-api-origin for cross-domain
  // development (e.g. data-api-origin="http://localhost:3000"). Otherwise,
  // assume the script is served from the Hub itself and use its origin.
  const originAttr = self.getAttribute('data-api-origin')
  let origin: string
  if (originAttr) {
    origin = originAttr.replace(/\/$/, '')
  }
  else {
    try {
      origin = new URL(self.src).origin
    }
    catch {
      origin = typeof window !== 'undefined' ? window.location.origin : ''
    }
  }

  // Avoid double-mounting if the script is accidentally included twice.
  if (document.getElementById('cc-chatbot-host')) return

  /**
   * Resolve a CSS custom property from the host page to a usable value + ink,
   * or null when the page doesn't expose it / it isn't a color.
   */
  function hostVariableColor(varName: string): { value: string, ink: string } | null {
    try {
      const raw = getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
      if (!raw) return null
      const rgb = resolveCssColor(raw)
      if (!rgb) return null
      return { value: raw, ink: readableInkFromRgb(rgb) }
    }
    catch {
      return null
    }
  }

  /**
   * Accent resolution. The Hub accent field holds either:
   *   - a CSS variable reference ("--ui-primary", "var(--brand-color)") —
   *     resolved from the host page, so any site can point the widget at its
   *     own design token;
   *   - a literal color ("#84cc16") — an explicit pick;
   *   - nothing — try the host's Nuxt UI primary (--ui-primary), then the
   *     stylesheet's built-in indigo fallback.
   */
  function resolveAccent(configAccent: string | null): { value: string, ink: string } | null {
    const trimmed = (configAccent ?? '').trim()
    const varName = trimmed.startsWith('var(') && trimmed.endsWith(')')
      ? trimmed.slice(4, -1).trim()
      : trimmed.startsWith('--') ? trimmed : null
    if (varName) return hostVariableColor(varName) ?? hostVariableColor('--ui-primary')
    if (trimmed) return { value: trimmed, ink: readableInk(trimmed) }
    return hostVariableColor('--ui-primary')
  }

  const config = await fetchConfig(origin, productSlug)
  if (!config || !config.enabled) return

  const host = document.createElement('div')
  host.id = 'cc-chatbot-host'
  document.body.appendChild(host)
  const shadow = host.attachShadow({ mode: 'open' })

  /**
   * Yield to anything that covers the page. Nuxt UI ships modals, slideovers
   * and drawers at z-index auto and orders them by DOM position, so no z-index
   * here can sit under them — any positive value covers the modal footer.
   *
   * Gate on the backdrop, not on the content. data-slot="overlay" is Nuxt UI's
   * own marker for the element that dims the page, rendered by Modal,
   * Slideover and Drawer (each `overlay` defaults to true). Two narrower
   * signals were tried and both over-fire: data-aria-hidden is also set by
   * Select, Combobox and DropdownMenu, and role="dialog" is also set by a
   * plain non-modal UPopover. A backdrop is the thing that actually claims the
   * page, so it is the thing worth yielding to.
   *
   * Match the overlay's existence, not its data-state. data-state flips to
   * "closed" the moment the dialog starts closing, but Presence keeps the node
   * mounted for the ~200ms fade-out — gating on state let the widget pop back
   * over a modal that was still on screen. The element unmounts when the
   * animation ends, so its presence tracks what the user actually sees.
   *
   * The rule lives in the host page, not the shadow sheet: a shadow stylesheet
   * cannot see the light DOM. :has() does not cross the shadow boundary, so
   * the widget's own markup can never match this rule.
   *
   * A dialog that renders no backdrop does not hide the widget: :overlay="false",
   * or :modal="false" (reka only renders the overlay when modal). That is
   * deliberate — reportar's map slideover is both, and the map stays usable
   * behind it.
   */
  const gateEl = document.createElement('style')
  gateEl.id = 'cc-chatbot-gate'
  gateEl.textContent = overlayGateCss
  document.head.appendChild(gateEl)

  const styleEl = document.createElement('style')
  styleEl.textContent = widgetStyles
  shadow.appendChild(styleEl)

  const rootEl = document.createElement('div')
  rootEl.className = 'cc-root'
  const accent = resolveAccent(config.accentColor)
  if (accent) {
    rootEl.style.setProperty('--cc-accent', accent.value)
    rootEl.style.setProperty('--cc-accent-ink', accent.ink)
  }
  shadow.appendChild(rootEl)

  const app = createApp(Chatbot, { config, origin, productSlug })
  app.mount(rootEl)
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      void bootstrap()
    })
  }
  else {
    void bootstrap()
  }
}
